import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../components/common/SafeIcon';

const { FiTarget, FiUsers, FiHeart, FiStar, FiShield, FiTrendingUp } = FiIcons;

const Culture = () => {
  const values = [
    { icon: FiTarget, title: 'Our Goal', description: 'To build up a one-stop filter product sourcing platform and professional service for global customers!', color: 'from-blue-500 to-blue-600' },
    { icon: FiUsers, title: 'Innovation & Management', description: 'We pursue scientific management and constant innovation to deliver excellence in every aspect of our business.', color: 'from-green-500 to-green-600' },
    { icon: FiHeart, title: 'Gratitude', description: 'We always remember and appreciate our customers, partners and friends who have supported our journey.', color: 'from-red-500 to-red-600' },
    { icon: FiStar, title: 'Brand Values', description: 'We value our brand, image and social reputation, just like we cherish our personal life.', color: 'from-yellow-500 to-yellow-600' },
    { icon: FiShield, title: 'Integrity', description: 'Honesty, trust and integrity are our moral norms that we strictly follow in all our business dealings.', color: 'from-purple-500 to-purple-600' }
  ];

  const principles = [
    { title: 'Quality First', description: 'Every product must meet our stringent quality standards before reaching our customers.', icon: FiStar },
    { title: 'Customer Focus', description: 'Our customers\' success is our success. We listen, adapt, and deliver solutions that exceed expectations.', icon: FiUsers },
    { title: 'Continuous Innovation', description: 'We invest in research and development to stay ahead of industry trends and technological advances.', icon: FiTrendingUp },
    { title: 'Global Responsibility', description: 'We are committed to environmental protection and sustainable development in all our operations.', icon: FiTarget }
  ];

  return (
    <div className="pt-16 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Culture & Philosophy
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            At Minipore, our values drive everything we do. We believe in building lasting relationships through trust, innovation, and unwavering commitment to quality.
          </p>
        </motion.div>

        {/* Core Values */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Core Values
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              These fundamental principles guide our decisions, shape our culture, and define who we are as a company.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className={`bg-gradient-to-br ${value.color} p-4 rounded-full w-fit mb-6 group-hover:scale-110 transition-transform`}>
                  <SafeIcon icon={value.icon} className="h-8 w-8 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">
                  {value.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {value.description}
                </p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Philosophy Statement */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-gradient-to-br from-blue-50 to-indigo-100 p-12 rounded-3xl"
          >
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                Our Philosophy
              </h2>
              <blockquote className="text-xl text-gray-700 leading-relaxed max-w-4xl mx-auto italic">
                "Our career key goal is to build up a one-stop filter product sourcing platform and the professional service for global customers. We pursue scientific management and constant innovation, always remembering our customers, partners and friends who have supported our journey."
              </blockquote>
              <div className="mt-8 flex justify-center">
                <div className="bg-white p-4 rounded-full shadow-lg">
                  <SafeIcon icon={FiTarget} className="h-8 w-8 text-blue-600" />
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Business Principles */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Our Business Principles
            </h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              These principles guide our daily operations and long-term strategic decisions.
            </p>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {principles.map((principle, index) => (
              <motion.div
                key={principle.title}
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: index * 0.2 }}
                className="bg-white p-8 rounded-2xl shadow-lg hover:shadow-xl transition-shadow"
              >
                <div className="flex items-start space-x-4">
                  <div className="bg-blue-100 p-3 rounded-full flex-shrink-0">
                    <SafeIcon icon={principle.icon} className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3">
                      {principle.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {principle.description}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Team Culture */}
        <section className="mb-20 bg-white rounded-2xl shadow-lg p-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Our Team Culture
                </h2>
                <p className="text-lg text-gray-600 mb-6">
                  We believe that our people are our greatest asset. Our culture fosters collaboration, creativity, and continuous learning, creating an environment where everyone can thrive and contribute to our collective success.
                </p>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiUsers} className="h-5 w-5 text-blue-600" />
                    <span className="text-gray-700">Collaborative team environment</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiTrendingUp} className="h-5 w-5 text-blue-600" />
                    <span className="text-gray-700">Continuous learning and development</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiHeart} className="h-5 w-5 text-blue-600" />
                    <span className="text-gray-700">Work-life balance and well-being</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <SafeIcon icon={FiStar} className="h-5 w-5 text-blue-600" />
                    <span className="text-gray-700">Recognition and career growth</span>
                  </div>
                </div>
              </div>
              <div>
                <img
                  src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600&h=400&fit=crop"
                  alt="Team collaboration"
                  className="rounded-2xl shadow-lg"
                />
              </div>
            </div>
          </motion.div>
        </section>

        {/* Environmental Commitment */}
        <section className="mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="bg-green-50 p-12 rounded-2xl"
          >
            <div className="text-center">
              <div className="bg-green-100 p-4 rounded-full w-fit mx-auto mb-6">
                <SafeIcon icon={FiShield} className="h-12 w-12 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Environmental Commitment
              </h2>
              <p className="text-lg text-gray-600 mb-6 max-w-3xl mx-auto">
                As an environmental protection enterprise, we are committed to sustainable practices and responsible manufacturing. Our water treatment solutions not only serve our customers but also contribute to global water conservation efforts.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 mb-2">100%</div>
                    <div className="text-gray-600">Eco-friendly Materials</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 mb-2">Zero</div>
                    <div className="text-gray-600">Waste Manufacturing</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-green-600 mb-2">50+</div>
                    <div className="text-gray-600">Countries Served</div>
                </div>
              </div>
            </div>
          </motion.div>
        </section>

        {/* CTA Section */}
        <section className="bg-blue-600 text-white p-12 rounded-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl font-bold mb-4">
              Join Our Mission
            </h2>
            <p className="text-xl text-blue-100 mb-8 max-w-3xl mx-auto">
              Be part of our journey to provide clean water solutions worldwide. Together, we can make a difference in global water treatment.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link
                to="/contact"
                className="bg-white text-blue-600 px-8 py-3 rounded-full font-semibold hover:bg-blue-50 transition-colors"
              >
                Partner with Us
              </Link>
              <Link
                to="/about"
                className="border-2 border-white text-white px-8 py-3 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition-colors"
              >
                Learn More
              </Link>
            </div>
          </motion.div>
        </section>
      </div>
    </div>
  );
};

export default Culture;